// Consultas al CRM con cache (React Query). Las llamadas se refrescan al instante cuando el tiempo real (/rt) avisa
// que termino una llamada o al registrarla desde el telefono; por si acaso, tambien cada 3 min (cada minuto si el
// tiempo real no esta conectado).
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as cdr from '../api/cdr';
import * as crm from '../api/crm';
import { getSession } from '../api/http';
import { agentesVoz } from '../api/voz';
import { tiempoRealConectado } from '../lib/tiempoReal';

const DIA = 86_400_000;

// Con API key Twenty permite 100 consultas por minuto para toda la empresa (y la central 3CX usa
// el mismo cupo): en ese modo se refresca menos seguido. Con sesion de usuario no hay ese limite.
const cada = (ms: number) => (getSession()?.modo === 'apikey' ? ms * 3 : ms);
// Lo que el tiempo real ya avisa no necesita sondeo seguido: con /rt conectado se espera `conRt`, sin el `sinRt`.
export const cadaVivo = (conRt: number, sinRt: number) => cada(tiempoRealConectado() ? conRt : sinRt);

export const K = {
  llamadas14: ['llamadas', '14d'] as const,
  llamadasMes: (y: number, m: number) => ['llamadas', 'mes', y, m] as const,
  llamadasPersona: (id: string) => ['llamadas', 'persona', id] as const,
  llamadasEmpresa: (id: string) => ['llamadas', 'empresa', id] as const,
  tareas: ['tareas'] as const,
  tipificaciones: ['tipificaciones'] as const,
  etapas: ['etapas'] as const,
  oportunidades: ['oportunidades'] as const,
  personas: (q: string) => ['personas', q] as const,
  empresas: (q: string) => ['empresas', q] as const,
  todasEmpresas: ['empresas', 'todas'] as const,
  persona: (id: string) => ['persona', id] as const,
  empresa: (id: string) => ['empresa', id] as const,
  miembros: ['miembros'] as const,
  tarifas: ['tarifas'] as const,
};

export function useLlamadasRecientes() {
  return useQuery({
    queryKey: K.llamadas14,
    queryFn: () => crm.llamadasRecientes(new Date(Date.now() - 14 * DIA), 1500),
    refetchInterval: () => cadaVivo(3 * 60_000, 60_000),
    staleTime: 15_000,
  });
}

export function useLlamadasMes(anio: number, mes: number) {
  return useQuery({
    queryKey: K.llamadasMes(anio, mes),
    queryFn: () => crm.llamadas({ desde: new Date(anio, mes, 1), hasta: new Date(anio, mes + 1, 1), max: 5000 }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export const useLlamadasPersona = (id: string) =>
  useQuery({ queryKey: K.llamadasPersona(id), queryFn: () => crm.llamadas({ personaId: id, max: 300 }), staleTime: 15_000 });

export const useLlamadasEmpresa = (id: string) =>
  useQuery({ queryKey: K.llamadasEmpresa(id), queryFn: () => crm.llamadas({ empresaId: id, max: 300 }), staleTime: 15_000 });

// Las tareas cambian casi siempre desde esta Consola (y esas acciones ya refrescan la lista): sondeo cada 3 min.
export const useTareas = () => useQuery({ queryKey: K.tareas, queryFn: crm.tareasPendientes, refetchInterval: () => cada(3 * 60_000), staleTime: 20_000 });

export const useTipificaciones = () => useQuery({ queryKey: K.tipificaciones, queryFn: crm.tipificaciones, staleTime: 10 * 60_000 });

export const useEtapas = () => useQuery({ queryKey: K.etapas, queryFn: crm.etapas, staleTime: 30 * 60_000 });

export const useOportunidades = () => useQuery({ queryKey: K.oportunidades, queryFn: crm.oportunidades, staleTime: 30_000 });

export const useMiembros = () => useQuery({ queryKey: K.miembros, queryFn: crm.miembros, staleTime: 10 * 60_000 });

// Nombre del agente por su extension de 3CX (la pasarela de voz sabe que usuario tiene cada extension).
// Sin dato, queda "Ext 502".
export function useAgentes() {
  const miembros = useMiembros();
  const voz = useQuery({ queryKey: ['agentes-voz'], queryFn: agentesVoz, staleTime: 10 * 60_000, retry: false });
  return useMemo(() => {
    const porExt = new Map<string, string>();
    for (const a of voz.data ?? []) {
      const m = miembros.data?.find((x) => x.email.toLowerCase() === a.email.toLowerCase());
      if (m?.nombre) porExt.set(a.extension, m.nombre);
    }
    return {
      nombre: (ext: string) => (ext ? (porExt.get(ext) ?? `Ext ${ext}`) : '—'),
      conExt: (ext: string) => (ext ? (porExt.has(ext) ? `${porExt.get(ext)} · ${ext}` : `Ext ${ext}`) : '—'),
    };
  }, [voz.data, miembros.data]);
}

export const useTarifas = () => useQuery({ queryKey: K.tarifas, queryFn: crm.tarifas, staleTime: 5 * 60_000 });

export const useTodasEmpresas = () => useQuery({ queryKey: K.todasEmpresas, queryFn: crm.todasLasEmpresas, staleTime: 5 * 60_000 });

export function usePersonas(q: string) {
  return useInfiniteQuery({
    queryKey: K.personas(q),
    queryFn: ({ pageParam }) => crm.buscarPersonas(q, 60, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasNextPage ? last.endCursor : undefined),
    placeholderData: keepPreviousData,
  });
}

export function useEmpresas(q: string) {
  return useInfiniteQuery({
    queryKey: K.empresas(q),
    queryFn: ({ pageParam }) => crm.buscarEmpresas(q, 60, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasNextPage ? last.endCursor : undefined),
    placeholderData: keepPreviousData,
  });
}

export const usePersona = (id: string) => useQuery({ queryKey: K.persona(id), queryFn: () => crm.persona(id) });
export const useEmpresa = (id: string) => useQuery({ queryKey: K.empresa(id), queryFn: () => crm.empresa(id) });

/* ---------- CDR de 3CX (Data Connector) ---------- */

export const useCdrEstado = () => useQuery({ queryKey: ['cdr', 'estado'], queryFn: cdr.estadoCdr, staleTime: 5 * 60_000, retry: 1 });

export const useCdrMes = (fuente: cdr.FuenteCdr, anio: number, mes: number, activo = true) =>
  useQuery({
    queryKey: ['cdr', fuente, anio, mes],
    queryFn: () => cdr.llamadasCdr(fuente, new Date(anio, mes, 1), new Date(anio, mes + 1, 1)),
    enabled: activo,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
